import React, { HTMLInputTypeAttribute, forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { Input as ShadcnInput } from '@/components/ui/Input';
import { Search } from 'lucide-react';
import { formatTestId } from '@/utils/helpers/formatTestId';
import { sanitizeText } from '@/utils/helpers/sanitizeText';

type Props = {
  id?: string;
  name?: string;
  value?: string | number | readonly string[];
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onInput?: (e: React.FormEvent<HTMLInputElement>) => void;
  placeholder?: string;
  errorMessage?: string;
  type?: HTMLInputTypeAttribute;
  isError?: boolean;
  autoFocus?: boolean;
  variant?: 'rowInput' | 'full' | 'search' | 'percent';
  disabled?: boolean;
  size?: 'small' | 'large' | 'full';
  text?: '' | 'center';
  label?: string;
  defaultValue?: string;
  style?: React.CSSProperties;
  autoComplete?: string;
  suffix?: string;
  testId?: string;
  readOnly?: boolean;
  searchIconRight?: boolean;
  sanitize?: boolean;
  customClassName?: string;
  hideSpinner?: boolean;
};

export const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      id = '',
      placeholder = '',
      errorMessage,
      type = 'text',
      isError,
      onChange,
      size = 'small',
      text = '',
      autoComplete,
      testId = 'generic',
      variant,
      label,
      suffix,
      readOnly,
      searchIconRight = false,
      sanitize = true,
      customClassName,
      hideSpinner,
      ...rest
    },
    ref,
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (sanitize) {
        const sanitizedValue = sanitizeText(e.target.value);
        e.target.value = sanitizedValue;
      }
      onChange?.(e);
    };

    return (
      <div className='flex flex-col w-full'>
        {label && (
          <label htmlFor={id} className='text-sm font-light text-foreground mb-1'>
            {label}
          </label>
        )}
        <div
          className={cn(
            'relative flex flex-col',
            variant === 'rowInput' && 'min-w-[325px] max-w-[325px]',
            variant === 'full' && 'w-full',
            (variant === 'percent' || suffix) && 'relative',
          )}
        >
          {variant === 'search' && (
            <Search
              className={cn(
                'absolute top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground',
                searchIconRight ? 'right-2.5' : 'left-2.5',
              )}
            />
          )}
          <ShadcnInput
            id={id}
            type={type}
            ref={ref}
            className={cn(
              size === 'small' && 'h-9',
              size === 'large' && 'h-11',
              size === 'full' && 'h-full',
              isError && 'border-destructive focus-visible:ring-destructive',
              variant === 'search' && !searchIconRight && 'pl-8',
              variant === 'search' && searchIconRight && 'pr-8',
              text === 'center' && 'text-center',
              (variant === 'percent' || suffix) && 'pr-8',
              readOnly &&
                'border-[#c1ccd4] bg-[#eaf5ff] text-[#2797e8] outline-none cursor-default',
              readOnly && isError && 'border-destructive bg-destructive/10 text-destructive',
              hideSpinner &&
                '[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
              customClassName,
            )}
            placeholder={placeholder}
            data-testid={formatTestId('input', testId)}
            {...rest}
            onChange={handleChange}
            autoComplete={autoComplete}
            readOnly={readOnly}
          />
          {(variant === 'percent' || suffix) && (
            <span className='absolute right-7 top-1/2 -translate-y-1/2 text-foreground pointer-events-none text-sm'>
              {suffix ?? '%'}
            </span>
          )}
        </div>
        {isError && errorMessage && (
          <span className='text-xs font-light text-destructive mt-1'>{errorMessage}</span>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
